import { useMemo, useState } from 'react';
import { getClass } from '../../../shared/catalog/classes';
import { MOD_DEFS, canEquip, type ModDef } from '../../../shared/catalog/mods';
import type { SelfState } from '../../../shared/protocol';
import { t, type Key } from '../i18n';
import { groupName, modDesc, modName } from '../i18n/catalog';
import { Icon } from './icons';

/** Choix d'une modification pour l'emplacement ouvert : seules les options compatibles sont proposées. */
export function ModPanel({ me, onPick, onLater }: { me: SelfState; onPick: (id: string) => void; onLater: () => void }) {
  const [open, setOpen] = useState<string | null>(null);
  const groups = useMemo(() => {
    const cls = getClass(me.cls);
    const options = Object.values(MOD_DEFS).filter((d) => d.slot === me.slot && canEquip(cls, me.level, me.mods, d.id));
    const by = new Map<string, ModDef[]>();
    for (const d of options) {
      const list = by.get(d.group) ?? [];
      list.push(d);
      by.set(d.group, list);
    }
    return [...by.entries()];
  }, [me.cls, me.level, me.mods, me.slot]);
  if (!me.slot || groups.length === 0) return null;
  return (
    <div className="mod-panel" data-testid="mod-panel">
      <div className="evo-title">{t(`slot_${me.slot}` as Key)}</div>
      <div className="mod-groups">
        {groups.map(([group, list]) => (
          <section key={group} className="mod-group">
            <button type="button" className="mod-group-title" onClick={() => setOpen(open === group ? null : group)} aria-expanded={open === group}>
              <Icon name={open === group ? 'left' : 'right'} size={14} className="icon mod-chevron" />
              {groupName(group)} <small className="num">({list.length})</small>
            </button>
            {open === group && (
              <div className="mod-options">
                {list.map((d) => (
                  <button key={d.id} type="button" className="mod-card" onClick={() => onPick(d.id)} data-testid={`mod-${d.id}`}>
                    <span className="evo-name">{modName(d)}</span>
                    <span className="evo-desc">{modDesc(d)}</span>
                    {d.teamOnly && <span className="mod-team">{t('modTeamOnly')}</span>}
                  </button>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>
      <button type="button" className="btn btn-ghost btn-small" onClick={onLater}>
        {t('evolveLater')}
      </button>
    </div>
  );
}

export function EquippedMods({ ids }: { ids: string[] }) {
  if (!ids.length) return null;
  return (
    <div className="mod-equipped">
      {ids.map((id) => (
        <span key={id} className="mod-chip" title={MOD_DEFS[id] ? modDesc(MOD_DEFS[id]) : undefined}>
          {MOD_DEFS[id] ? modName(MOD_DEFS[id]) : id}
        </span>
      ))}
    </div>
  );
}
